import { useAuth } from "@/contexts/AuthContext";
import { Role } from "@/types/auth";

// Re-export Role type for convenience
export type { Role };

export const ROLES: Role[] = Object.values(Role);

/**
 * Hook to get current user's role and role-related utilities
 */
export function useRole() {
  const { role, user, hasRole } = useAuth();

  const canManageUser = (targetRole: Role): boolean => {
    if (!role) return false;

    if (role === Role.ADMIN) {
      return true; // Admin can manage everyone
    } else if (role === Role.SENIOR) {
      // Senior can manage everyone except other Seniors and Admins
      return targetRole !== Role.ADMIN && targetRole !== Role.SENIOR;
    } else {
      return false; // Other roles cannot manage users
    }
  };

  const isAdmin = () => role === Role.ADMIN;
  const isAdminOrSenior = () => role === Role.ADMIN || role === Role.SENIOR;
  const isManager = () => !!user?.is_manager;
  const isAdminSeniorOrComptable = () => {
    if (!role) return false;
    const r = String(role).toLowerCase();
    return r === "admin" || r === "senior" || r === "comptable";
  };

  return {
    role: role as Role | null,
    hasRole,
    canManageUser,
    isAdmin,
    isAdminOrSenior,
    isManager,
    isAdminSeniorOrComptable,
  };
}
